import React, { useCallback, useState } from "react";
import { Slider } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import { TeamSide, YardConverter } from "@prestosports/prestostats-core/Common";
import { BallPositionTooltip } from "./BallPosition";
import { useTeamsContext } from "../../../state/teams/context";
import ballPositionStyles from "../styles/BallPosition.styles";
import fieldRulerStyles from "../styles/FieldRuler.styles";
import { getSafeOrDefault } from "../../../util/object-util";
import { ColoredMarker } from "./field-selectors/FieldMarkersEditor";

// Team-side rail colors for a field ruler. Matches the in-game field bar:
// unswapped (reversed=true) puts the visitor on the left, home on the right.
export const teamRailColors = (homeTeam, awayTeam, reversed, theme) => {
	const homeColor = getSafeOrDefault(
		() => homeTeam.color,
		theme.palette.teams.home
	);
	const visitorColor = getSafeOrDefault(
		() => awayTeam.color,
		theme.palette.teams.visitor
	);
	return {
		leftColor: reversed ? visitorColor : homeColor,
		rightColor: reversed ? homeColor : visitorColor
	};
};

// Disabled-state palette for the read-only reference markers.
const REFERENCE_MARKER_FILL = "#ECECEC";
const REFERENCE_MARKER_TEXT = "#9E9E9E";

const convertTo = (value, max, reversed) => {
	return reversed ? max - value : value;
};

const ThumbComponent = (props) => {
	const { state: teamState } = useTeamsContext();
	const awayTeam = teamState.awayTeam();
	const homeTeam = teamState.homeTeam();

	const { "aria-valuenow": value, "aria-valuemax": max, reversed } = props;
	const yardConverter = new YardConverter(max);
	const { teamSide, yard } = yardConverter.convertFromFieldPosition(
		convertTo(value, max, reversed)
	);

	return (
		// eslint-disable-next-line react/jsx-props-no-spreading
		<span {...props}>
			<Box style={{ position: "absolute", bottom: 14 }}>
				<BallPositionTooltip
					title={
						teamSide === TeamSide.Home
							? homeTeam?.abbrName?.substring(0, 3) || TeamSide.Home
							: awayTeam?.abbrName?.substring(0, 3) || TeamSide.Visitor
					}
					subtitle={yard}
				/>
			</Box>
		</span>
	);
};

const FieldPositionPicker = ({
	value,
	fieldLength,
	onChange = () => {},
	reversed,
	referenceMarkers = [],
	snapTo = []
}) => {
	const classes = ballPositionStyles({});
	const theme = useTheme();
	const { state: teamState } = useTeamsContext();
	const awayTeam = teamState.awayTeam();
	const homeTeam = teamState.homeTeam();
	const railClasses = fieldRulerStyles(
		teamRailColors(homeTeam, awayTeam, reversed, theme)
	);
	const half = fieldLength / 2;

	// Controlled with a local drag draft so a committed value can snap: the
	// thumb follows the finger while dragging and jumps to the snapped value
	// on release (an uncontrolled slider would stay where it was dropped).
	const [draft, setDraft] = useState(null);
	const shown = draft ?? convertTo(Number(value), fieldLength, reversed);

	const sideTitle = (teamSide) => {
		return teamSide === TeamSide.Home
			? homeTeam?.abbrName?.substring(0, 3) || TeamSide.Home
			: awayTeam?.abbrName?.substring(0, 3) || TeamSide.Visitor;
	};

	// Snap to the nearest allowed target (field coordinates) when provided —
	// zone-to-gain targets are the configured lines, not arbitrary yards.
	const snapValue = (fieldValue) => {
		let result = fieldValue;
		if (snapTo.length) {
			result = snapTo.reduce((prev, curr) =>
				Math.abs(curr - fieldValue) < Math.abs(prev - fieldValue) ? curr : prev
			);
		}
		return result;
	};

	const marks = [];
	for (let i = 0; i <= fieldLength; i += 10) {
		marks.push({
			value: i,
			label: i < half ? i : fieldLength - i
		});
	}

	// Identity-stable thumb: a new component type per render would remount the
	// thumb on every drag step now that the slider is controlled.
	const thumbComponent = useCallback(
		(props) => <ThumbComponent {...props} reversed={reversed} />,
		[reversed]
	);

	return (
		<Box style={{ paddingTop: 40 }}>
			<Box className={classes.fieldOverlayWrapper}>
				{/* Read-only reference markers (e.g. the configured first-down
				    spots): same pentagon as the draggable thumb, greyed out as
				    a disabled state. Anchored like the live thumb (left edge
				    at value center - 6px, pentagon floating above the rail). */}
				{referenceMarkers.length > 0 && (
					<Box className={classes.markerOverlay}>
						{referenceMarkers.map((marker) => {
							const yardConverter = new YardConverter(fieldLength);
							const { teamSide, yard } =
								yardConverter.convertFromFieldPosition(marker);
							const pct =
								(convertTo(marker, fieldLength, reversed) / fieldLength) * 100;
							return (
								<Box
									key={`ref-${marker}`}
									style={{
										position: "absolute",
										left: `${pct}%`,
										// Same height as the editable thumb's pentagon,
										// anchored from the TOP (the wrapper can extend below
										// the slider root, so bottom anchoring drifts): the
										// thumb span bottoms at y=20, its marker floats 14px
										// above that (bottom y=6), pentagon is 47px tall ->
										// top at y=-41 relative to the slider top.
										top: -41,
										transform: "translateX(-50%)"
									}}
								>
									<ColoredMarker
										title={sideTitle(teamSide)}
										subtitle={yard}
										fill={REFERENCE_MARKER_FILL}
										textColor={REFERENCE_MARKER_TEXT}
									/>
								</Box>
							);
						})}
					</Box>
				)}
				<Slider
					className={railClasses.teamRail}
					min={0}
					max={fieldLength}
					track={false}
					value={shown}
					step={1}
					valueLabelDisplay="off"
					ThumbComponent={thumbComponent}
					marks={marks}
					onChange={(_, v) => setDraft(v)}
					onChangeCommitted={(e, v) => {
						setDraft(null);
						onChange(e, snapValue(convertTo(v, fieldLength, reversed)));
					}}
				/>
			</Box>
		</Box>
	);
};

export default FieldPositionPicker;
